import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import { PackagesService } from '../../../api/PackagesService';
import { PageTitle } from '../../../common/components/PageTitle';
import { WithSidebar } from '../WithSidebar';
import { BookingSummary } from '../BookingSummary';
import { BookingCategoryItem } from './BookingCategoryItem';
import { Spinner } from '../../../common/components/Spinner';
import { catchCallback } from '../../../api/request';
import { bookingClean } from '../BookingPage/bookingClean';
import { findLang } from '../../../common/models/lang';
import { useDictionary } from '../../../common/contexts/DictionaryContext';
import { SERVICES } from '../../../constants';

const BookingCategoryList = ({
    url,
    data: { booking, firstTransfer, secondTransfer },
    update: { updateFirstTransfer },
}) => {
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState(undefined);
    const [error, setError] = useState(undefined);
    const { t, i18n } = useTranslation();
    const dictionary = useDictionary();

    const loadList = () => {
        const request = PackagesService.getServicePackage({
            ...bookingClean(booking),
            languageId: dictionary?.languages.find(({ name }) => name === findLang(i18n.language).name).id,
        });

        request
            .then((data) => {
                setList(data);
            })
            .catch(
                catchCallback((error) => {
                    setError(error);
                }),
            )
            .finally(() => setLoading(false));

        return () => {
            request.cancel();
        };
    };

    useEffect(loadList, [dictionary]);

    const content = (() => {
        if (loading) {
            return <Spinner />;
        }

        if (error) {
            return <Alert severity="error">{t('error.bookingCategoriesNotLoaded')}</Alert>;
        }

        if (!list) {
            return null;
        }

        const transferPassengers = (t) => (t ? (t.adults || 0) + (t.children || 0) + (t.kids || 0) : 0);
        // The upsell button only sets legroom on the first leg (the reducer copies it to the
        // second leg, clamped to that leg's own passenger count). Size it to THIS leg's
        // passengers so an asymmetric round trip never offers more seats than the leg has.
        const totalPassengers = transferPassengers(firstTransfer);
        const legroomService = (dictionary?.services || []).find((s) => s.internalCode === SERVICES.EXTRA_LEGROOM);

        return (
            <Stack sx={{ gap: 3 }}>
                {list.map((item) => (
                    <BookingCategoryItem
                        key={`${item.carCode}_${item.serviceType}`}
                        item={item}
                        updateFirstTransfer={updateFirstTransfer}
                        url={url}
                        transfer={firstTransfer}
                        legroomService={legroomService}
                        totalPassengers={totalPassengers}
                    />
                ))}
            </Stack>
        );
    })();

    const sidebar = (
        <BookingSummary
            sx={{ maxWidth: { xs: 350 } }}
            url={url}
            firstTransfer={firstTransfer}
            secondTransfer={secondTransfer}
        />
    );

    return (
        <>
            <PageTitle>{t('transfer-select')}</PageTitle>
            <Box sx={(theme) => theme.mixins.box(theme)}>
                <WithSidebar content={content} sidebar={sidebar} />
            </Box>
        </>
    );
};

export { BookingCategoryList };
